import { employeeServiceApi } from "../../app/employeeServiceApi";
import { API_URL } from "../../constants/apiUrl";
import { GetQueryArgsType, PatchQueryArgsType } from "../../types";
import {
  GetAllKpiParams,
  GetAllKpiResponseType,
  GetOwnKpiDepartmentParams,
  GetOwnKpiDepartmentResponseType,
  GetOwnKpiParams,
  GetOwnKpiResponseType,
  GetOwnSingleKpiResponseType,
  GetSingleKpiResponseType,
} from "../../types/performance";

export const performanceManagementApi = employeeServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    createKpi: builder.mutation({
      query: (data) => ({
        url: API_URL.createKpi,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetKPI", "GetOwnKPI", "GetOwnKPIByDepartment"];
        return [];
      },
    }),
    getAllKpi: builder.query<
      GetAllKpiResponseType,
      GetQueryArgsType<GetAllKpiParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.getAllKpi,
        ...queryArgs,
      }),
      providesTags: ["GetKPI"],
    }),
    getSingleKpi: builder.query<GetSingleKpiResponseType, string>({
      query: (id) => ({
        url: `${API_URL.getSingleKpi}/${id}`,
      }),
      providesTags: (_, __, id) => [{ type: "GetSingleKPI", id }],
    }),
    createAppraisal: builder.mutation<void, PatchQueryArgsType<unknown>>({
      query: ({ body, id }) => ({
        url: `${API_URL.createAppraisal}/${id}`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, _, { id }) => {
        if (result) return ["GetKPI", { type: "GetSingleKPI", id }];
        return [];
      },
    }),
    updateKpi: builder.mutation<void, PatchQueryArgsType<unknown>>({
      query: ({ body, id }) => ({
        url: `${API_URL.updateKpi}/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, _, { id }) => {
        if (result)
          return [
            "GetKPI",
            "GetOwnKPI",
            "GetOwnKPIByDepartment",
            { type: "GetSingleKPI", id },
            { type: "GetOwnSingleKPI", id },
          ];
        return [];
      },
    }),
    getOwnKpi: builder.query<
      GetOwnKpiResponseType,
      GetQueryArgsType<GetOwnKpiParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.getOwnKpi,
        ...queryArgs,
      }),
      providesTags: ["GetOwnKPI"],
    }),
    getOwnKpiByDepartment: builder.query<
      GetOwnKpiDepartmentResponseType,
      GetQueryArgsType<GetOwnKpiDepartmentParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.getOwnKpiByDepartment,
        ...queryArgs,
      }),
      providesTags: ["GetOwnKPIByDepartment"],
    }),
    getOwnSingleKpi: builder.query<GetOwnSingleKpiResponseType, string>({
      query: (id) => ({
        url: `${API_URL.getOwnSingleKpi}/${id}`,
      }),
      providesTags: (_, __, id) => [{ type: "GetOwnSingleKPI", id }],
    }),
    lineManagerSubmitKpi: builder.mutation<void, PatchQueryArgsType<unknown>>({
      query: ({ body, id }) => ({
        url: `${API_URL.lineManagerSubmitKpi}/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, _, { id }) => {
        if (result)
          return [
            "GetOwnKPI",
            "GetOwnKPIByDepartment",
            { type: "GetSingleKPI", id },
            { type: "GetOwnSingleKPI", id },
          ];
        return [];
      },
    }),
    submitOwnKpi: builder.mutation({
      query: (body) => ({
        url: API_URL.submitOwnKpi,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetOwnKPI", "GetOwnKPIByDepartment"];
        return [];
      },
    }),
    ownAppraisalSubmit: builder.mutation<void, PatchQueryArgsType<unknown>>({
      query: ({ body, id }) => ({
        url: `${API_URL.ownAppraisalSubmit}/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, _, { id }) => {
        if (result)
          return [
            "GetOwnKPI",
            "GetOwnKPIByDepartment",
            { type: "GetSingleKPI", id },
            { type: "GetOwnSingleKPI", id },
          ];
        return [];
      },
    }),
    teamAppraisalSubmit: builder.mutation<void, PatchQueryArgsType<unknown>>({
      query: ({ body, id }) => ({
        url: `${API_URL.teamAppraisalSubmit}/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, _, { id }) => {
        if (result)
          return [
            "GetOwnKPI",
            "GetOwnKPIByDepartment",
            { type: "GetSingleKPI", id },
            { type: "GetOwnSingleKPI", id },
          ];
        return [];
      },
    }),
    recommendationForPromotion: builder.mutation<
      void,
      PatchQueryArgsType<unknown>
    >({
      query: ({ body, id }) => ({
        url: `${API_URL.recommendationForPromotion}/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, _, { id }) => {
        if (result)
          return [
            "GetOwnKPI",
            "GetOwnKPIByDepartment",
            { type: "GetSingleKPI", id },
            { type: "GetOwnSingleKPI", id },
          ];
        return [];
      },
    }),
  }),
});

export const {
  useCreateKpiMutation,
  useGetAllKpiQuery,
  useGetSingleKpiQuery,
  useCreateAppraisalMutation,
  useUpdateKpiMutation,
  useGetOwnKpiQuery,
  useGetOwnKpiByDepartmentQuery,
  useGetOwnSingleKpiQuery,
  useLineManagerSubmitKpiMutation,
  useSubmitOwnKpiMutation,
  useOwnAppraisalSubmitMutation,
  useTeamAppraisalSubmitMutation,
  useRecommendationForPromotionMutation,
} = performanceManagementApi;
